// components/chat/RecordingsPanel.jsx
import React, { useState, useEffect, useContext } from "react";
import { Search, Disc, Phone, Play, Pause, Download, Copy, Trash2, Loader2 } from "lucide-react";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "../ui/Toast";

const NEON     = "#00FF99";
const API_BASE = process.env.REACT_APP_API_BASE || "http://localhost:8000";

const fmt = secs => {
  if (!secs) return "";
  const s = parseInt(secs);
  return `${String(Math.floor(s/60)).padStart(2,"0")}:${String(s%60).padStart(2,"0")}`;
};

const niceDate = iso => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleDateString([], { day:"2-digit", month:"short", year:"numeric" });
};

const daysLeft = iso => {
  if (!iso) return 0;
  const diff = new Date(iso) - new Date();
  return Math.max(0, Math.ceil(diff / 86400000));
};

export default function RecordingsPanel({ onClose }) {
  const { user }                = useContext(AuthContext);
  const [recordings, setRecs]   = useState([]);
  const [loading,    setLoading]= useState(true);
  const [playing,    setPlaying]= useState(null);
  const [deleting,   setDeleting]= useState(null);
  const [search,     setSearch] = useState("");

  useEffect(() => {
    if (!user) return;
    setLoading(true);
    fetch(`${API_BASE}/api/calling/recordings`, {
      headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
    })
      .then(r => r.ok ? r.json() : { recordings:[] })
      .then(d => setRecs(d.recordings || []))
      .catch(() => setRecs([]))
      .finally(() => setLoading(false));
  }, [user]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this recording? This cannot be undone.")) return;
    setDeleting(id);
    try {
      await fetch(`${API_BASE}/api/calling/recordings/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
      });
      setRecs(prev => prev.filter(r => r.id !== id));
      if (playing === id) setPlaying(null);
    } catch(e) { console.error(e); }
    setDeleting(null);
  };

  const filtered = recordings.filter(r => {
    if (!search) return true;
    const names = (r.participant_names || []).join(" ").toLowerCase();
    return names.includes(search.toLowerCase()) || r.room_id?.includes(search.toLowerCase());
  });

  return (
    <div style={{
      display:"flex", flexDirection:"column", height:"100%",
      background:"#0B0B0B", fontFamily:"'DM Sans',system-ui,sans-serif",
    }}>
      {/* Header */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"16px 20px", borderBottom:"1px solid rgba(0,255,153,0.15)",
        flexShrink:0,
      }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div>
            <p style={{ color:"#fff", fontWeight:700, fontSize:15 }}>Recordings</p>
            <p style={{ color:"rgba(255,255,255,0.3)", fontSize:11 }}>
              {recordings.length} recording{recordings.length !== 1 ? "s" : ""}
            </p>
          </div>
        </div>
        <button onClick={onClose}
          style={{ background:"none", border:"none", color:"rgba(255,255,255,0.3)", cursor:"pointer", fontSize:18, padding:"4px 8px", borderRadius:8 }}>✕</button>
      </div>

      {/* Search */}
      <div style={{ padding:"12px 16px", borderBottom:"1px solid rgba(255,255,255,0.06)", flexShrink:0 }}>
        <div style={{ position:"relative" }}>
          <span style={{ position:"absolute", left:10, top:"50%", transform:"translateY(-50%)", display:"flex", color:"rgba(255,255,255,0.35)", pointerEvents:"none" }}><Search size={14} strokeWidth={1.9} /></span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by participant or room…"
            style={{
              width:"100%", background:"rgba(255,255,255,0.05)",
              border:"1px solid rgba(255,255,255,0.1)", borderRadius:10,
              padding:"8px 10px 8px 30px", color:"#fff", fontSize:12,
              outline:"none", boxSizing:"border-box",
            }}
          />
        </div>
      </div>

      {/* List */}
      <div style={{ flex:1, overflowY:"auto", padding:"12px 16px" }}>
        {loading && (
          <div style={{ display:"flex", alignItems:"center", justifyContent:"center", height:120 }}>
            <div style={{ width:28, height:28, borderRadius:"50%", border:`2px solid ${NEON}`, borderTopColor:"transparent", animation:"spin 0.8s linear infinite" }} />
          </div>
        )}

        {!loading && filtered.length === 0 && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", height:200, gap:12 }}>
            <Disc size={40} strokeWidth={1.8} color="rgba(255,255,255,0.25)" />
            <p style={{ color:"rgba(255,255,255,0.2)", fontSize:13, textAlign:"center" }}>
              {search ? "No recordings match your search" : "No recordings yet.\nStart a call and hit Record!"}
            </p>
          </div>
        )}

        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          {filtered.map(rec => {
            const names   = (rec.participant_names || []).join(", ");
            const days    = daysLeft(rec.expires_at);
            const isPlaying = playing === rec.id;
            const expiring  = days <= 7;

            return (
              <div key={rec.id} style={{
                borderRadius:14, overflow:"hidden",
                background:"rgba(255,255,255,0.04)",
                border:`1px solid ${isPlaying ? NEON+"40" : "rgba(255,255,255,0.08)"}`,
                transition:"border-color 0.2s",
              }}>
                {/* Recording info */}
                <div style={{ padding:"12px 14px" }}>
                  <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between", gap:8, marginBottom:6 }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <p style={{ color:"#fff", fontWeight:600, fontSize:13, marginBottom:2, truncate:true }}>
                        <Phone size={12} strokeWidth={1.9} style={{ verticalAlign:"-2px" }} /> {names || "Call Recording"}
                      </p>
                      <p style={{ color:"rgba(255,255,255,0.3)", fontSize:11 }}>
                        {niceDate(rec.created_at)}
                        {rec.duration_secs && rec.duration_secs !== "0" ? ` · ${fmt(rec.duration_secs)}` : ""}
                      </p>
                    </div>
                    {/* Expiry badge */}
                    <span style={{
                      padding:"2px 8px", borderRadius:20, fontSize:10, fontWeight:600, flexShrink:0,
                      background: expiring ? "rgba(239,68,68,0.15)" : "rgba(255,255,255,0.06)",
                      color: expiring ? "#f87171" : "rgba(255,255,255,0.35)",
                      border: expiring ? "1px solid rgba(239,68,68,0.3)" : "1px solid rgba(255,255,255,0.08)",
                    }}>
                      {days}d left
                    </span>
                  </div>

                  {/* Video player */}
                  {isPlaying && (
                    <div style={{ marginBottom:10, borderRadius:10, overflow:"hidden", background:"#000" }}>
                      <video
                        src={rec.url}
                        controls autoPlay
                        style={{ width:"100%", maxHeight:200, display:"block" }}
                        onEnded={() => setPlaying(null)}
                      />
                    </div>
                  )}

                  {/* Actions */}
                  <div style={{ display:"flex", gap:6, flexWrap:"wrap" }}>
                    {/* Play/Pause */}
                    <button
                      onClick={() => setPlaying(isPlaying ? null : rec.id)}
                      style={{
                        display:"flex", alignItems:"center", gap:5,
                        padding:"6px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                        background: isPlaying ? `${NEON}18` : "rgba(255,255,255,0.08)",
                        border: isPlaying ? `1px solid ${NEON}40` : "1px solid rgba(255,255,255,0.1)",
                        color: isPlaying ? NEON : "rgba(255,255,255,0.7)",
                        cursor:"pointer",
                      }}>
                      {isPlaying
                        ? <><Pause size={12} strokeWidth={1.9} /> Pause</>
                        : <><Play size={12} strokeWidth={1.9} /> Play</>}
                    </button>

                    {/* Download */}
                    <button
                      onClick={() => {
                        const a = document.createElement("a");
                        a.href = rec.url;
                        a.download = `recording-${rec.id}.mp4`;
                        a.target = "_blank";
                        a.rel = "noopener noreferrer";
                        document.body.appendChild(a);
                        a.click();
                        document.body.removeChild(a);
                      }}
                      style={{
                        display:"flex", alignItems:"center", gap:5,
                        padding:"6px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                        background:"rgba(255,255,255,0.08)",
                        border:"1px solid rgba(255,255,255,0.1)",
                        color:"rgba(255,255,255,0.7)", cursor:"pointer",
                      }}>
                      <Download size={12} strokeWidth={1.9} /> Download
                    </button>

                    {/* Copy link */}
                    <button
                      onClick={() => navigator.clipboard.writeText(rec.url).then(() => toast.success("Link copied!"))}
                      style={{
                        display:"flex", alignItems:"center", gap:5,
                        padding:"6px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                        background:"rgba(255,255,255,0.08)",
                        border:"1px solid rgba(255,255,255,0.1)",
                        color:"rgba(255,255,255,0.7)", cursor:"pointer",
                      }}>
                      <Copy size={12} strokeWidth={1.9} /> Copy Link
                    </button>

                    {/* Delete */}
                    <button
                      onClick={() => handleDelete(rec.id)}
                      disabled={deleting === rec.id}
                      style={{
                        display:"flex", alignItems:"center", gap:5,
                        padding:"6px 12px", borderRadius:8, fontSize:11, fontWeight:600,
                        background:"rgba(239,68,68,0.08)",
                        border:"1px solid rgba(239,68,68,0.2)",
                        color:"#f87171", cursor:"pointer", marginLeft:"auto",
                      }}>
                      {deleting === rec.id
                        ? <Loader2 size={12} strokeWidth={1.9} style={{ animation:"spin 0.8s linear infinite" }} />
                        : <><Trash2 size={12} strokeWidth={1.9} /> Delete</>}
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`@keyframes spin { to { transform:rotate(360deg); } }`}</style>
    </div>
  );
}